import { useEffect, useRef } from "react";

/**
 * Lightweight 2D-canvas dotted particle sphere.
 * Fibonacci distribution, slow rotation, depth-based opacity & size.
 * Recreated from the provided sphere asset for performance + seamless motion.
 */
export const ParticleSphereCanvas = ({ className = "" }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    let width = 0;
    let height = 0;
    let radius = 0;
    let cx = 0;
    let cy = 0;
    let points = [];

    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    const COUNT = isMobile ? 540 : 1150;

    const buildPoints = () => {
      points = [];
      const offset = 2 / COUNT;
      const increment = Math.PI * (3 - Math.sqrt(5));
      for (let i = 0; i < COUNT; i++) {
        const y = i * offset - 1 + offset / 2;
        const r = Math.sqrt(Math.max(0, 1 - y * y));
        const phi = i * increment;
        points.push([Math.cos(phi) * r, y, Math.sin(phi) * r]);
      }
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      radius = Math.min(width, height) * 0.42;
      cx = width > 768 ? width * 0.68 : width * 0.5;
      cy = height * 0.5;
    };

    buildPoints();
    resize();

    let angleY = 0;
    const angleX = -0.42;
    let last = performance.now();

    const drawFrame = () => {
      ctx.clearRect(0, 0, width, height);
      const cosY = Math.cos(angleY);
      const sinY = Math.sin(angleY);
      const cosX = Math.cos(angleX);
      const sinX = Math.sin(angleX);

      for (let i = 0; i < points.length; i++) {
        const p = points[i];
        const x = p[0];
        const y = p[1];
        const z = p[2];
        const x1 = x * cosY - z * sinY;
        const z1 = x * sinY + z * cosY;
        const y1 = y * cosX - z1 * sinX;
        const z2 = y * sinX + z1 * cosX;
        const depth = (z2 + 1) / 2; // 0 (back) .. 1 (front)
        const px = cx + x1 * radius;
        const py = cy + y1 * radius;
        const size = 0.4 + depth * 1.5;
        const alpha = 0.06 + depth * 0.42;
        ctx.beginPath();
        ctx.fillStyle = "rgba(214,170,92," + alpha.toFixed(3) + ")";
        ctx.arc(px, py, size, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const loop = (now) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      angleY += dt * 0.05;
      drawFrame();
      rafRef.current = requestAnimationFrame(loop);
    };

    if (reduced) {
      drawFrame();
    } else {
      rafRef.current = requestAnimationFrame(loop);
    }

    const onResize = () => {
      resize();
      if (reduced) drawFrame();
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="particle-sphere"
      aria-hidden="true"
      className={"h-full w-full opacity-[0.6] mix-blend-screen " + className}
    />
  );
};
